import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';

/**
 * Reusable 3-dot menu component.
 * 
 * Usage:
 *   <MoreMenu actions={[
 *     { label: 'Edit', icon: <Edit size={15} />, onClick: handleEdit },
 *     { label: 'Delete', icon: <Trash2 size={15} />, onClick: handleDelete, danger: true },
 *   ]} />
 */
export default function MoreMenu({ actions = [], size = 18, className = '' }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="p-1.5 rounded-lg text-gray-soft hover:text-charcoal hover:bg-cream transition-colors"
        data-testid="more-menu-trigger"
        aria-label="More actions"
      >
        <MoreVertical size={size} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 bg-white border border-beige rounded-lg shadow-lg py-1 z-20 min-w-[140px]"
          data-testid="more-menu-dropdown"
        >
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                action.onClick(e);
              }}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
                action.danger
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-charcoal hover:bg-cream'
              }`}
              data-testid={action.testId || `more-menu-${action.label?.toLowerCase()}`}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
